import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiDownload } from 'react-icons/fi';
import ChildSelector from '../../components/parent/ChildSelector';
import { useParentChild } from '../../contexts/ParentChildContext';
import api from '../../services/api';

const formatMonth = (mois) => {
  const [y, m] = mois.split('-');
  const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};

const ParentDocumentsPage = () => {
  const { inscriptionId, selectedChild } = useParentChild();
  const [releveMonths, setReleveMonths] = useState([]);
  const [bulletinPeriods, setBulletinPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!inscriptionId) return;
    setLoading(true);
    Promise.all([
      api.get(`/parent-portal/children/${inscriptionId}/releve-months`),
      api.get(`/parent-portal/children/${inscriptionId}/bulletin-periods`),
    ])
      .then(([releves, bulletins]) => {
        if (releves.data.success) setReleveMonths(releves.data.data);
        if (bulletins.data.success) setBulletinPeriods(bulletins.data.data);
      })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [inscriptionId]);

  const downloadPdf = async (type, id) => {
    setDownloading(`${type}-${id}`);
    try {
      let url;
      if (type === 'releve') {
        url = `/parent-portal/children/${inscriptionId}/releve/${id}`;
      } else {
        url = `/parent-portal/children/${inscriptionId}/bulletin?periode_id=${id}`;
      }
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const childName = selectedChild ? `${selectedChild.prenom}_${selectedChild.nom}` : 'eleve';
      link.download = type === 'releve'
        ? `Releve_${childName}_${id}.pdf`
        : `Bulletin_${childName}_${id}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4">
      <ChildSelector />
      <div>
        <h2 className="text-xl font-bold text-slate-900">Documents</h2>
        <p className="text-sm text-slate-500">Relevés de notes et bulletins</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Relevés de notes</h3>
            {releveMonths.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun relevé disponible</p>
            ) : (
              <ul className="space-y-2">
                {releveMonths.map((mois) => (
                  <li key={mois} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-700">{formatMonth(mois)}</span>
                    <button
                      onClick={() => downloadPdf('releve', mois)}
                      disabled={downloading === `releve-${mois}`}
                      className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    >
                      <FiDownload size={14} />
                      {downloading === `releve-${mois}` ? '...' : 'PDF'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Bulletins</h3>
            {bulletinPeriods.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun bulletin disponible pour le moment</p>
            ) : (
              <ul className="space-y-2">
                {bulletinPeriods.map((p) => (
                  <li key={p.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-700">{p.periode}</span>
                    <button
                      onClick={() => downloadPdf('bulletin', p.id)}
                      disabled={downloading === `bulletin-${p.id}`}
                      className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    >
                      <FiDownload size={14} />
                      {downloading === `bulletin-${p.id}` ? '...' : 'PDF'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentDocumentsPage;
